import type { MealItem, MealSlot } from '@cadencia/contracts';
import { requireFood } from '../catalog/foods.js';
import { findRecipe } from '../catalog/recipes.js';
import { energyFloorKcal, SAFETY_LIMITS } from '../safety/limits.js';
import { roundTo } from '../units/round.js';
import {
  itemsMacros,
  recipeScaleBounds,
  recipeToItems,
  roundMacros,
  sumMacros,
  ZERO_MACROS,
} from './composition.js';
import { dayProteinFloorG, MAX_MEAL_SHARE_OF_DAY } from './meal-split.js';
import { addAdjustmentItems, closesWithinTolerance } from './menu.js';
import { MACRO_RULES, type MacroTargets } from './targets.js';

/**
 * Reajuste del dia (docs/NUTRITION_ENGINE.md §7).
 *
 * Lo que ya se ha comido no se discute: se reparte lo que queda entre las
 * comidas que faltan, con topes. Las restricciones existen para que el motor no
 * proponga compensaciones absurdas, que es la forma mas rapida de que alguien
 * deje de fiarse del plan. **Nunca** se propone saltarse una comida ni ayunar.
 */

export const ADJUST_RULES = {
  /** Ninguna comida escala mas de esto respecto a su version planificada. */
  maxMealScaleChange: 0.35,
  /** Fraccion del exceso que se absorbe hoy; el resto se reparte (§7.1.3). */
  absorbTodayShare: 0.6,
  /** Dias siguientes entre los que se reparte el resto. */
  carryOverDays: 2,
  /** Tope de arrastre por dia, en fraccion del objetivo de ese dia. */
  maxCarryOverPerDay: 0.08,
  scaleStep: 0.01,
  scaleRounds: 3,
  /** Paso y pasadas del reparto alimento a alimento dentro de cada comida. */
  itemStep: 0.05,
  itemRounds: 12,
} as const;

/**
 * Lo que se anade cuando el dia se quedaria por debajo del suelo de proteina.
 *
 * Dos condiciones, y las dos importan. Que sean **dominados por la proteina**
 * —menos de 7 kcal por gramo de proteina— para que proteger el suelo cueste
 * las minimas kcal posibles: el dia ya va sobrado, y un puñado de almendras
 * traeria la proteina con veintisiete kcal por gramo. Y que se puedan **anadir
 * sin cocinar** a una comida que ya esta planificada, que es lo que descarta a
 * la pechuga de pollo aunque sea igual de magra: nadie se pone a la plancha
 * para cuadrar una cena que ya tenia su plato.
 */
export const PROTEIN_TOPPERS: readonly { foodSlug: string; maxAmount: number; step: number }[] = [
  { foodSlug: 'queso-batido-0', maxAmount: 500, step: 25 },
  { foodSlug: 'yogur-proteico', maxAmount: 450, step: 25 },
  { foodSlug: 'clara-huevo', maxAmount: 400, step: 25 },
  { foodSlug: 'atun-lata-natural', maxAmount: 200, step: 20 },
];

export interface LoggedMealInput {
  slot: MealSlot;
  /** Lo realmente consumido. */
  macros: MacroTargets;
  /** Lo que estaba previsto para esa comida. */
  plannedMacros: MacroTargets;
}

export interface PendingMealInput {
  slot: MealSlot;
  recipeSlug: string | null;
  items: readonly MealItem[];
  plannedMacros: MacroTargets;
}

export interface AdjustedMeal {
  slot: MealSlot;
  items: MealItem[];
  macros: MacroTargets;
  scale: number;
  adjusted: boolean;
  adjustmentReason: string | null;
}

export interface DayAdjustResult {
  meals: AdjustedMeal[];
  /** Totales del dia contando lo consumido y lo ajustado. */
  totals: MacroTargets;
  /** kcal que NO se compensan hoy y pasan a los dias siguientes. */
  carryOverKcal: number;
  /** Reparto propuesto del arrastre, en kcal por dia siguiente. */
  carryOverPerDay: number[];
  /** kcal que no caben ni hoy ni en el arrastre; se explican, no se ocultan. */
  residualKcal: number;
  closesWithinTolerance: boolean;
  reasonCodes: string[];
  explanation: string;
}

interface CurvaComida {
  entrada: PendingMealInput;
  min: number;
  max: number;
  materializar: (factor: number) => MealItem[];
}

function curvaDe(entrada: PendingMealInput): CurvaComida {
  const recipe = entrada.recipeSlug === null ? undefined : findRecipe(entrada.recipeSlug);
  const limite = ADJUST_RULES.maxMealScaleChange;

  if (recipe) {
    const bounds = recipeScaleBounds(recipe);
    return {
      entrada,
      min: Math.max(bounds.min, 1 - limite),
      max: Math.min(bounds.max, 1 + limite),
      materializar: (factor) => recipeToItems(recipe, factor),
    };
  }

  // Comida sin receta (modificada a mano o «comere fuera»): se escala entera.
  return {
    entrada,
    min: 1 - limite,
    max: 1 + limite,
    materializar: (factor) =>
      entrada.items.map((item) => ({
        ...item,
        amount: item.unit === 'unit' ? Math.max(1, Math.round(item.amount * factor)) : roundTo(item.amount * factor, 1),
      })),
  };
}

function macrosDe(curva: CurvaComida, factor: number): MacroTargets {
  return itemsMacros(curva.materializar(factor));
}

/**
 * Error del reparto, con la proteina como caso aparte.
 *
 * Quedarse corto de proteina y pasarse no cuestan lo mismo, y con un peso
 * simetrico el optimizador la vendia siempre: la tolerancia de kcal es la mas
 * estrecha, asi que ante «llegar a las kcal» o «llegar a la proteina» elegia
 * las kcal y el dia acababa por debajo del suelo. El orden de prioridad del
 * §4.1 es el contrario —proteina >= grasa minima > hidratos—, asi que
 * quedarse por debajo del objetivo de proteina se penaliza seis veces mas que
 * cualquier otra desviacion, y pasarse casi no se penaliza.
 */
function errorPonderado(actual: MacroTargets, objetivo: MacroTargets): number {
  const rel = (valor: number, meta: number): number => (meta <= 0 ? 0 : (valor - meta) / meta);
  const proteina = rel(actual.proteinG, objetivo.proteinG);
  return (
    (rel(actual.kcal, objetivo.kcal) / 0.03) ** 2 +
    (proteina < 0 ? (proteina / 0.01) ** 2 : (proteina / 0.15) ** 2) +
    (rel(actual.carbsG, objetivo.carbsG) / 0.08) ** 2 +
    (rel(actual.fatG, objetivo.fatG) / 0.08) ** 2
  );
}

/**
 * Margen de cada alimento dentro de una comida que hay que recortar.
 *
 * Escalar la comida entera por un factor unico no sirve para proteger la
 * proteina: recortar 700 kcal de la cena se lleva por delante el pescado igual
 * que la patata. Medido: tras un desayuno de 1.800 kcal con solo 30 g de
 * proteina, el dia acababa en 93 g contra un suelo de 129,6. Por eso el
 * recorte se hace alimento a alimento y la fuente proteica apenas se toca, que
 * es lo que haria cualquiera con el plato delante.
 */
const MARGEN_ITEM = {
  proteico: { min: 0.9, max: 1.25 },
  resto: { min: 0.5, max: 1.3 },
} as const;

/** Un alimento cuenta como proteico si la proteina manda en su energia. */
function esProteico(item: MealItem): boolean {
  const food = requireFood(item.foodSlug);
  return food.kcal100 > 0 && (4 * food.protein100) / food.kcal100 >= 0.4;
}

function escalarItem(item: MealItem, factor: number): MealItem {
  return {
    ...item,
    amount:
      item.unit === 'unit'
        ? Math.max(1, Math.round(item.amount * factor))
        : roundTo(item.amount * factor, 1),
  };
}

/**
 * Reparte el ajuste dentro de cada comida, alimento a alimento.
 *
 * Descenso por coordenadas con primera mejora, sin aleatoriedad (ADR-007). La
 * comida entera sigue sin poder salirse de su margen de escalado: lo que cambia
 * es que ahora puede llegar a ese margen recortando lo que sobra en vez de
 * encogerlo todo por igual.
 */
function afinarItems(
  base: readonly (readonly MealItem[])[],
  curvas: readonly CurvaComida[],
  factoresIniciales: readonly number[],
  objetivo: MacroTargets,
): MealItem[][] {
  const planes = base.map((items, indice) => items.map(() => factoresIniciales[indice] ?? 1));

  const materializar = (): MealItem[][] =>
    base.map((items, indice) =>
      items.map((item, posicion) => escalarItem(item, planes[indice]![posicion]!)),
    );

  const totalDe = (comidas: readonly MealItem[][]): MacroTargets =>
    comidas.length === 0 ? { ...ZERO_MACROS } : sumMacros(comidas.map((items) => itemsMacros(items)));

  /** La comida no puede salirse de su margen de escalado en energia. */
  const dentroDeSuMargen = (comidas: readonly MealItem[][], indice: number): boolean => {
    const planeada = itemsMacros(base[indice]!).kcal;
    if (planeada <= 0) return true;
    const ratio = itemsMacros(comidas[indice]!).kcal / planeada;
    return ratio >= curvas[indice]!.min - 1e-6 && ratio <= curvas[indice]!.max + 1e-6;
  };

  let mejor = errorPonderado(totalDe(materializar()), objetivo);

  for (let ronda = 0; ronda < ADJUST_RULES.itemRounds; ronda += 1) {
    let mejoro = false;

    for (const [indice, items] of base.entries()) {
      for (const [posicion, item] of items.entries()) {
        const margen = esProteico(item) ? MARGEN_ITEM.proteico : MARGEN_ITEM.resto;

        for (const delta of [ADJUST_RULES.itemStep, -ADJUST_RULES.itemStep]) {
          const previo = planes[indice]![posicion]!;
          const propuesto = roundTo(
            Math.min(Math.max(previo + delta, margen.min), margen.max),
            3,
          );
          if (propuesto === previo) continue;

          planes[indice]![posicion] = propuesto;
          const comidas = materializar();
          const error = errorPonderado(totalDe(comidas), objetivo);
          if (error < mejor - 1e-9 && dentroDeSuMargen(comidas, indice)) {
            mejor = error;
            mejoro = true;
            break;
          }
          planes[indice]![posicion] = previo;
        }
      }
    }

    if (!mejoro) break;
  }

  return materializar();
}

function pasos(curva: CurvaComida): number[] {
  const lista: number[] = [];
  for (let f = curva.min; f <= curva.max + 1e-9; f += ADJUST_RULES.scaleStep) {
    lista.push(roundTo(f, 3));
  }
  return lista.length > 0 ? lista : [1];
}

export interface DayAdjustInput {
  dayTarget: MacroTargets;
  bodyWeightKg: number;
  rmrKcal: number;
  logged: readonly LoggedMealInput[];
  pending: readonly PendingMealInput[];
  /** Objetivo de kcal de los dias siguientes, para acotar el arrastre. */
  nextDaysKcal?: readonly number[];
}

/**
 * Recalcula las comidas pendientes del dia.
 *
 * Si el desvio es grande no se compensa entero hoy: se absorbe el 60 % y el
 * resto se reparte en los dos dias siguientes con tope del 8 % diario. Meterlo
 * todo en la cena obligaria a raciones que nadie se come, y el plan que no se
 * come no es un plan.
 */
export function adjustDay(input: DayAdjustInput): DayAdjustResult {
  const reasonCodes: string[] = [];
  const consumido = input.logged.length === 0
    ? { ...ZERO_MACROS }
    : sumMacros(input.logged.map((comida) => comida.macros));
  const previstoConsumido = input.logged.length === 0
    ? { ...ZERO_MACROS }
    : sumMacros(input.logged.map((comida) => comida.plannedMacros));

  const desvio = consumido.kcal - previstoConsumido.kcal;

  let carryOverKcal = 0;
  let absorberHoy = desvio;
  if (Math.abs(desvio) > 0) {
    absorberHoy = desvio * ADJUST_RULES.absorbTodayShare;
    carryOverKcal = desvio - absorberHoy;
    if (Math.abs(desvio) > 0.001) reasonCodes.push('deviation_split_across_days');
  }

  const previstoPendiente = input.pending.length === 0
    ? { ...ZERO_MACROS }
    : sumMacros(input.pending.map((comida) => comida.plannedMacros));

  // Objetivo de lo que queda: lo previsto menos la parte del desvio que toca
  // absorber hoy. El resto viaja a los dias siguientes. Se hace macro a macro,
  // porque haberse pasado con el pan y haberse pasado con el aceite no piden la
  // misma correccion aunque sumen las mismas kcal.
  const absorber = (real: number, previsto: number): number =>
    (real - previsto) * ADJUST_RULES.absorbTodayShare;

  let objetivoPendiente: MacroTargets = {
    kcal: previstoPendiente.kcal - absorberHoy,
    proteinG: Math.max(
      0,
      previstoPendiente.proteinG - absorber(consumido.proteinG, previstoConsumido.proteinG),
    ),
    carbsG: Math.max(
      0,
      previstoPendiente.carbsG - absorber(consumido.carbsG, previstoConsumido.carbsG),
    ),
    fatG: Math.max(0, previstoPendiente.fatG - absorber(consumido.fatG, previstoConsumido.fatG)),
  };

  // Suelo calorico: el dia entero nunca baja de aqui (§7.1.4). Si el objetivo
  // de lo que queda dejaria el dia por debajo, se sube y se explica.
  const sueloDia = energyFloorKcal(input.rmrKcal);
  if (consumido.kcal + objetivoPendiente.kcal < sueloDia) {
    objetivoPendiente = { ...objetivoPendiente, kcal: sueloDia - consumido.kcal };
    reasonCodes.push('energy_floor_protected');
  }

  // Suelo de proteina del dia.
  const sueloProteina = dayProteinFloorG(input.bodyWeightKg);
  const proteinaMinimaPendiente = Math.max(0, sueloProteina - consumido.proteinG);
  if (objetivoPendiente.proteinG < proteinaMinimaPendiente) {
    objetivoPendiente = { ...objetivoPendiente, proteinG: proteinaMinimaPendiente };
    reasonCodes.push('protein_floor_protected');
  }

  // Grasa: ni por debajo del minimo ni por encima del 35 % de las kcal.
  const minGrasaDia = Math.max(
    SAFETY_LIMITS.macros.minFatGPerKg * input.bodyWeightKg,
    (SAFETY_LIMITS.macros.minFatPctOfKcal * input.dayTarget.kcal) / 9,
  );
  const maxGrasaDia = (MACRO_RULES.maxFatPctOfKcal * input.dayTarget.kcal) / 9;
  const minGrasaPendiente = Math.max(0, minGrasaDia - consumido.fatG);
  const maxGrasaPendiente = Math.max(0, maxGrasaDia - consumido.fatG);
  if (objetivoPendiente.fatG < minGrasaPendiente) {
    objetivoPendiente = { ...objetivoPendiente, fatG: minGrasaPendiente };
    reasonCodes.push('fat_floor_protected');
  }
  if (objetivoPendiente.fatG > maxGrasaPendiente) {
    objetivoPendiente = { ...objetivoPendiente, fatG: maxGrasaPendiente };
    reasonCodes.push('fat_cap_protected');
  }

  const curvas = input.pending.map(curvaDe);
  const factores = curvas.map(() => 1);

  // El tope del 45 % es una regla de REPARTO: existe para que una comida no se
  // trague el dia cuando hay otras. Con una sola comida al dia —esquema de una
  // comida, o el resto ya registrado— esa comida es el dia, asi que el tope es
  // inalcanzable por definicion y aplicarlo dejaba el ajuste sin ningun factor
  // valido: se quedaba en 1 y no se reajustaba nada. Se mira el numero de
  // comidas del DIA, no de las pendientes.
  const comidasDelDia = input.logged.length + input.pending.length;
  const aplicaTopePorComida = comidasDelDia > 1;

  for (let ronda = 0; ronda < ADJUST_RULES.scaleRounds; ronda += 1) {
    for (let i = 0; i < curvas.length; i += 1) {
      let mejorFactor = factores[i]!;
      let mejorError = Number.POSITIVE_INFINITY;
      for (const factor of pasos(curvas[i]!)) {
        factores[i] = factor;
        const total = sumMacros(curvas.map((curva, j) => macrosDe(curva, factores[j]!)));
        // Ninguna comida individual puede pasar del 45 % de las kcal del dia.
        const propia = macrosDe(curvas[i]!, factor);
        if (aplicaTopePorComida && propia.kcal > input.dayTarget.kcal * MAX_MEAL_SHARE_OF_DAY) {
          continue;
        }
        const error = errorPonderado(total, objetivoPendiente);
        if (error < mejorError) {
          mejorError = error;
          mejorFactor = factor;
        }
      }
      factores[i] = mejorFactor;
    }
  }

  // El factor por comida deja el dia en el sitio; el reparto por alimento
  // decide QUE se recorta dentro de cada comida.
  const planeadas = curvas.map((curva) => curva.materializar(1));
  const afinadas = afinarItems(planeadas, curvas, factores, objetivoPendiente);

  const meals: AdjustedMeal[] = curvas.map((curva, indice) => {
    const items = afinadas[indice]!;
    const macros = roundMacros(itemsMacros(items));
    const planeada = itemsMacros(planeadas[indice]!).kcal;
    const factor = planeada > 0 ? macros.kcal / planeada : 1;
    const cambia = Math.abs(factor - 1) > 0.02;
    return {
      slot: curva.entrada.slot,
      items,
      macros,
      scale: roundTo(factor, 2),
      adjusted: cambia,
      adjustmentReason: cambia
        ? factor > 1
          ? 'Se ha subido esta comida para llegar al objetivo del día.'
          : 'Se ha recortado esta comida para compensar lo ya consumido.'
        : null,
    };
  });

  let totalPendiente = meals.length === 0 ? { ...ZERO_MACROS } : sumMacros(meals.map((m) => m.macros));

  // Si con el escalado no llega, se anaden items de ajuste antes de rendirse.
  if (meals.length > 0 && !closesWithinTolerance(totalPendiente, objetivoPendiente)) {
    const ajuste = addAdjustmentItems(totalPendiente, objetivoPendiente);
    if (ajuste.items.length > 0) {
      const destino = meals.reduce((mejor, actual) =>
        actual.macros.kcal < mejor.macros.kcal ? actual : mejor,
      );
      destino.items = [...destino.items, ...ajuste.items];
      destino.macros = roundMacros(itemsMacros(destino.items));
      destino.adjusted = true;
      destino.adjustmentReason =
        destino.adjustmentReason ?? 'Se ha añadido un complemento para cerrar el día.';
      totalPendiente = sumMacros(meals.map((m) => m.macros));
      reasonCodes.push('adjustment_items_added');
    }
  }

  // Suelo de proteina del DIA, comprobado sobre el resultado y no solo sobre el
  // objetivo. Que `objetivoPendiente` respete el suelo no basta: escalar las
  // comidas para meterlas en las kcal que quedan tambien les baja la proteina,
  // asi que la propuesta puede acabar por debajo del suelo con el objetivo
  // intacto. Lo vio un test de la capa de aplicacion, no uno del motor: un
  // almuerzo enorme de arroz y aceite dejaba el dia en 117 g con el suelo en
  // 129,6. CA-4.4 dice que ninguna propuesta baja de ahi, asi que se completa
  // con alimentos magros aunque cueste kcal, y se explica.
  if (meals.length > 0) {
    const proteinaDelDia = consumido.proteinG + totalPendiente.proteinG;
    const falta = sueloProteina - proteinaDelDia;
    if (falta > 0.5) {
      const relleno = rellenoProteico(falta);
      if (relleno.length > 0) {
        const destino = meals.reduce((mejor, actual) =>
          actual.macros.kcal < mejor.macros.kcal ? actual : mejor,
        );
        destino.items = [...destino.items, ...relleno];
        destino.macros = roundMacros(itemsMacros(destino.items));
        destino.adjusted = true;
        destino.adjustmentReason =
          'Se ha añadido proteína magra para no bajar del mínimo del día.';
        totalPendiente = sumMacros(meals.map((m) => m.macros));
        reasonCodes.push('protein_floor_enforced');
      } else {
        reasonCodes.push('protein_floor_unreachable');
      }
    }
  }

  const totals = roundMacros(sumMacros([consumido, totalPendiente]));

  // Reparto del arrastre con tope del 8 % diario.
  const siguientes = input.nextDaysKcal ?? [];
  const carryOverPerDay: number[] = [];
  let pendienteDeRepartir = carryOverKcal;
  for (let dia = 0; dia < ADJUST_RULES.carryOverDays; dia += 1) {
    const objetivoDia = siguientes[dia] ?? input.dayTarget.kcal;
    const tope = objetivoDia * ADJUST_RULES.maxCarryOverPerDay;
    const parte = Math.sign(pendienteDeRepartir) * Math.min(Math.abs(pendienteDeRepartir), tope);
    carryOverPerDay.push(Math.round(parte));
    pendienteDeRepartir -= parte;
  }
  if (Math.abs(pendienteDeRepartir) >= 1) reasonCodes.push('carry_over_capped');

  const cierra = closesWithinTolerance(totals, input.dayTarget);
  if (!cierra) reasonCodes.push('day_partial_after_adjust');

  return {
    meals,
    totals,
    carryOverKcal: Math.round(carryOverKcal),
    carryOverPerDay,
    residualKcal: Math.round(pendienteDeRepartir),
    closesWithinTolerance: cierra,
    reasonCodes,
    explanation: explicar(desvio, reasonCodes, carryOverKcal),
  };
}

/**
 * Alimentos magros suficientes para cubrir `faltaG` gramos de proteina.
 *
 * Se recorre la lista en orden —del mas magro por gramo de proteina al menos—
 * y se toma de cada uno lo justo. Devuelve vacio si ni con todos se llega:
 * mejor decir que no se puede que fingir que si.
 */
function rellenoProteico(faltaG: number): MealItem[] {
  const items: MealItem[] = [];
  let pendiente = faltaG;

  for (const topper of PROTEIN_TOPPERS) {
    if (pendiente <= 0.5) break;
    const food = requireFood(topper.foodSlug);
    if (food.protein100 <= 0) continue;

    const necesario = (pendiente / food.protein100) * 100;
    const cantidad = Math.min(
      topper.maxAmount,
      Math.ceil(necesario / topper.step) * topper.step,
    );
    if (cantidad <= 0) continue;

    items.push({ foodSlug: topper.foodSlug, amount: cantidad, unit: food.unitBase });
    pendiente -= (food.protein100 * cantidad) / 100;
  }

  return pendiente > 0.5 ? [] : items;
}

function explicar(desvio: number, reasonCodes: readonly string[], arrastre: number): string {
  const partes: string[] = [];
  if (Math.abs(desvio) < 25) {
    partes.push('Lo registrado se parece a lo previsto, así que el resto del día casi no cambia.');
  } else if (desvio > 0) {
    partes.push(
      `Has comido unas ${Math.round(desvio)} kcal más de lo previsto en lo que llevas de día.`,
    );
  } else {
    partes.push(
      `Has comido unas ${Math.round(-desvio)} kcal menos de lo previsto en lo que llevas de día.`,
    );
  }
  if (Math.abs(arrastre) >= 25) {
    partes.push(
      `Se compensa el 60 % hoy y el resto (${Math.abs(Math.round(arrastre))} kcal) en los dos días siguientes, para no forzar las comidas que quedan.`,
    );
  }
  if (reasonCodes.includes('protein_floor_protected')) {
    partes.push('La proteína no baja del mínimo, así que ese recorte sale de los otros macros.');
  }
  if (reasonCodes.includes('protein_floor_enforced')) {
    partes.push(
      'Se ha añadido proteína magra a lo que queda: el mínimo diario de proteína manda sobre el objetivo de kcal.',
    );
  }
  if (reasonCodes.includes('protein_floor_unreachable')) {
    partes.push(
      'Con lo que queda del día no se llega al mínimo de proteína ni añadiendo complementos; mañana se compensa.',
    );
  }
  if (reasonCodes.includes('energy_floor_protected')) {
    partes.push('El día se queda por encima del objetivo antes que bajar del suelo calórico.');
  }
  if (reasonCodes.includes('day_partial_after_adjust')) {
    partes.push('Aun así el día no cierra del todo dentro de la tolerancia; queda explicado arriba.');
  }
  return partes.join(' ');
}
